// Shared utility helpers for Game Plan Tracker components.

export const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
export const GAMEPLAN_PASSWORD = 'GamePlan123!';

export const formatCurrency = (value) => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatCompactCurrency = (value) => {
  if (!value && value !== 0) return '$0';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1000000) return `${sign}$${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(0)}K`;
  return `${sign}$${abs}`;
};

export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

export const getStageLabel = (stage) => {
  const labels = {
    pending_submission: 'Pending Submission',
    submitted: 'Submitted',
    approved: 'Approved',
    declined: 'Declined',
    settled: 'Settled',
  };
  return labels[stage] || stage;
};

export const getStageColor = (stage) => {
  const colors = {
    pending_submission: 'bg-amber-500',
    submitted: 'bg-blue-500',
    approved: 'bg-emerald-500',
    declined: 'bg-red-500',
    settled: 'bg-green-700',
  };
  return colors[stage] || 'bg-gray-500';
};

export const getCurrentMonth = () => {
  // Use local date (not UTC) so Tasmania users on May 1 see May 2026,
  // not April 2026 which would leak from UTC on late evenings/early mornings.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// ─────────────────────────────────────────────────────────────────────────
// Revenue helpers
// ─────────────────────────────────────────────────────────────────────────
// Upfront commission rate on the loan amount. Kept as a single constant so
// the whole app moves together if commercials ever change.
export const UPFRONT_COMMISSION_RATE = 0.006; // 0.60%

// Estimated upfront commission earned on a deal (Loan Amount × 0.60%).
export const dealUpfront = (deal) =>
  Math.max(0, Number(deal?.est_loan_amount || 0) * UPFRONT_COMMISSION_RATE);

// Brokerage fee actually being charged on this deal. If the broker toggled
// "Yes" and entered an amount, we return that; otherwise 0.
export const dealBrokerage = (deal) =>
  deal?.brokerage_fee_charged ? Math.max(0, Number(deal?.brokerage_fee_amount || 0)) : 0;

// Total estimated revenue for a single deal.
export const dealRevenue = (deal) => dealUpfront(deal) + dealBrokerage(deal);

// Aggregate revenue for a list of deals with an upfront / brokerage split so
// the WeekColumn can show a real breakdown, not just a single total.
export const weekRevenueBreakdown = (deals) => {
  let upfront = 0;
  let brokerage = 0;
  (deals || []).forEach((d) => {
    upfront += dealUpfront(d);
    brokerage += dealBrokerage(d);
  });
  return { upfront, brokerage, total: upfront + brokerage, count: (deals || []).length };
};

// ─────────────────────────────────────────────────────────────────────────
//  Deal urgency + KPI helpers (Board section overhaul, Feb 2026)
// ─────────────────────────────────────────────────────────────────────────

// Whole days between now and an ISO date (positive = past, negative = future).
export const daysSince = (isoDate) => {
  if (!isoDate) return null;
  const start = new Date(isoDate);
  if (isNaN(start.getTime())) return null;
  const now = new Date();
  return Math.floor((now - start) / 86400000);
};

// Days remaining until a future date (positive = future, negative = past).
export const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const end = new Date(isoDate);
  if (isNaN(end.getTime())) return null;
  const now = new Date();
  // Zero out time so "today" reads as 0, not -1
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTarget = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((startOfTarget - startOfToday) / 86400000);
};

// Urgency ladder used by the Board:
//   critical → past its expected settle date OR (submitted >14d + settles in ≤7d)
//   urgent   → expected to settle within the next 7 days but not yet approved
//   watch    → submitted 7-14 days ago with no approval movement
export const computeUrgency = (deal) => {
  const stage = (deal?.status_stage || deal?.current_stage || '').toLowerCase();
  // A settled deal is done — nothing to flag.
  if (stage === 'settled') return null;

  const settleDate = deal?.projected_settlement_date;
  const submittedDays = daysSince(deal?.submitted_date || deal?.created_at);
  const untilSettle = daysUntil(settleDate);

  // CRITICAL: past expected settle date, or submitted long ago and settling soon
  if (settleDate && untilSettle !== null && untilSettle < 0 && stage !== 'declined') {
    return { level: 'critical', reason: 'past-settle', daysPastSettle: -untilSettle };
  }
  if (
    submittedDays !== null && submittedDays >= 14 &&
    untilSettle !== null && untilSettle >= 0 && untilSettle <= 7 &&
    stage !== 'approved' && stage !== 'declined'
  ) {
    return { level: 'critical', reason: 'stuck-near-settle', submittedDays, untilSettle };
  }

  // URGENT: settling within 7 days but not yet approved
  if (
    untilSettle !== null && untilSettle >= 0 && untilSettle <= 7 &&
    !['approved', 'settled'].includes(stage)
  ) {
    return { level: 'urgent', reason: 'settle-this-week', untilSettle };
  }

  // WATCH: 7-14 days in submitted stage (existing "warming")
  if (['submitted', 'pending_submission'].includes(stage)) {
    if (submittedDays !== null && submittedDays >= 14) {
      return { level: 'critical', reason: 'stale', submittedDays };
    }
    if (submittedDays !== null && submittedDays >= 7) {
      return { level: 'watch', reason: 'warming', submittedDays };
    }
  }
  return null;
};

// 4-segment progress rail — 0-based index of the deal's current stage.
export const stageProgressionIndex = (stage) => {
  const map = {
    pending_submission: 0,
    submitted: 1,
    approved: 2,
    settled: 3,
    declined: -1,
    not_proceeding: -1,
  };
  return map[(stage || '').toLowerCase()] ?? 0;
};

// Weighted pipeline value — each deal's loan amount discounted by its
// probability of settling (typical broker heuristic).
export const stageProbability = (stage) => {
  const map = {
    pending_submission: 0.4,
    submitted: 0.65,
    approved: 0.9,
    settled: 1.0,
    declined: 0,
    not_proceeding: 0,
  };
  return map[(stage || '').toLowerCase()] ?? 0.5;
};

// Colour palette for lead-source chips (concise, non-clashing).
export const leadSourceChipClass = (source) => {
  const s = (source || '').toLowerCase();
  if (s.includes('referral')) return 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300';
  if (s.includes('repeat')) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300';
  if (s.includes('brokerage')) return 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300';
  if (s.includes('online') || s.includes('web')) return 'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300';
  if (s.includes('social')) return 'bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300';
  return 'bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-300';
};

// Get first day of the ISO week (Monday) and Sunday for a given date.
const _weekBoundsForToday = () => {
  const now = new Date();
  const day = now.getDay(); // 0 Sun … 6 Sat
  const diffToMon = (day === 0 ? -6 : 1 - day);
  const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diffToMon);
  const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
  return { mon, sun };
};

// Board-level KPIs — computed client-side from board deals.
// Returns { velocity, velocityTrend, conversionRate, amountAtRisk, momentum,
//           readyToSettleCount, readyToSettleAmount }
export const computeBoardKpis = (currentDeals, prevDeals) => {
  const list = Array.isArray(currentDeals) ? currentDeals : [];
  const prev = Array.isArray(prevDeals) ? prevDeals : [];

  const avgDaysToApproval = (deals) => {
    const spans = deals
      .filter((d) => d.submitted_date && d.approved_date)
      .map((d) => daysSince(d.submitted_date) - daysSince(d.approved_date))
      .filter((n) => Number.isFinite(n) && n >= 0);
    if (!spans.length) return null;
    return spans.reduce((a, b) => a + b, 0) / spans.length;
  };

  const velocity = avgDaysToApproval(list);
  const prevVelocity = avgDaysToApproval(prev);
  let velocityTrend = null;
  if (velocity !== null && prevVelocity !== null && prevVelocity > 0) {
    // Faster = fewer days = "up" (green). Show percentage change.
    const delta = ((prevVelocity - velocity) / prevVelocity) * 100;
    velocityTrend = {
      direction: delta > 1 ? 'up' : delta < -1 ? 'down' : 'flat',
      pct: Math.abs(delta),
    };
  }

  const submittedCount = list.filter((d) => ['submitted', 'approved', 'settled'].includes((d.status_stage || '').toLowerCase())).length;
  const approvedCount = list.filter((d) => ['approved', 'settled'].includes((d.status_stage || '').toLowerCase())).length;
  const conversionRate = submittedCount > 0 ? (approvedCount / submittedCount) * 100 : null;

  const atRiskDeals = list.filter((d) => {
    const u = computeUrgency(d);
    return u && (u.level === 'critical' || u.level === 'urgent');
  });
  const amountAtRisk = atRiskDeals.reduce((sum, d) => sum + (d.est_loan_amount || 0), 0);

  // Momentum: deals whose stage moved in the last 7 days
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 86400000);
  const momentum = list.filter((d) => {
    const changed = d.stage_last_changed_at ? new Date(d.stage_last_changed_at) : null;
    return changed && !isNaN(changed.getTime()) && changed >= sevenDaysAgo;
  }).length;

  // Ready-to-settle THIS WEEK (Mon-Sun): approved deals with projected_settlement_date in current week
  const { mon, sun } = _weekBoundsForToday();
  const readyToSettle = list.filter((d) => {
    if ((d.status_stage || '').toLowerCase() !== 'approved') return false;
    if (!d.projected_settlement_date) return false;
    const dt = new Date(d.projected_settlement_date);
    return !isNaN(dt.getTime()) && dt >= mon && dt <= sun;
  });

  return {
    velocity,
    velocityTrend,
    conversionRate,
    amountAtRisk,
    atRiskCount: atRiskDeals.length,
    momentum,
    readyToSettleCount: readyToSettle.length,
    readyToSettleAmount: readyToSettle.reduce((sum, d) => sum + (d.approved_amount || d.est_loan_amount || 0), 0),
  };
};

// Given a list of deals in a week, sum weighted pipeline value.
export const weekWeightedValue = (deals) =>
  (deals || [])
    .filter((d) => !['declined', 'not_proceeding'].includes((d.status_stage || '').toLowerCase()))
    .reduce((sum, d) => sum + (d.est_loan_amount || 0) * stageProbability(d.status_stage), 0);

// Previous YYYY-MM for a given month string.
export const previousMonthOf = (yyyyMm) => {
  const [y, m] = (yyyyMm || '').split('-').map(Number);
  if (!y || !m) return null;
  const prev = new Date(y, m - 2, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
};

export const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = -6; i <= 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    // Build YYYY-MM from LOCAL components — toISOString() would shift the
    // value by one month for Australia (UTC+10/+11) and mis-label every option.
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    options.push({
      value,
      label: d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }),
    });
  }
  return options;
};
